/**
 * The matter hub (docs/ux/matter-hub.md): where a matter is worked. Header
 * with the reference, the chip, the meta row and the guarded actions with
 * the engine's answers; nine tabs; Overview, Outstanding items, Audit and
 * Casefile (milestone 5) and LOI (milestone 4) are live, the rest state
 * honestly which milestone brings them.
 */

import { EVENT_DISPLAY, eventLabel, reportTypeLabel, statusLabel, type StatusEvent } from '@beam/core/status';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { say } from '@/app/announcer';
import { ApiError } from '@/app/api';
import { useOnline } from '@/app/online';
import { personaHome, useSession } from '@/app/session';
import type { Appointment, SideStepKey, TransitionOption } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { DaysCell } from '@/components/matter/DaysCell';
import { FlagText, activeFlags } from '@/components/matter/FlagText';
import { StatusChip } from '@/components/matter/StatusChip';
import { recordRecent } from '@/components/shell/MatterSearch';
import { PageHeader } from '@/components/shell/PageHeader';
import { QueueTabs } from '@/components/shell/QueueTabs';
import { ErrorPanel } from '@/components/shell/states';
import { useShellWidth } from '@/components/shell/context';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { dateTime, shortDate } from '@/lib/format';
import { ActionCluster, MoreMenu, PhoneActions } from './ActionCluster';
import { BOOKING_KINDS, FORM_KINDS, layoutActions } from './actions';
import { BookingForm, type BookingValues } from './BookingForm';
import { BookingTab } from './booking/BookingTab';
import { gateSubState } from '../pipeline/columns';
import { MatterNotFound, MatterRecordPanel, NotYetTab, OutstandingItems, OutstandingItemsSummary, SideStepsChecklist } from './panels';
import { TransitionForm } from './TransitionForm';
import { AuditTable, TransitionLog } from './TransitionLog';
import { useAddItem, useMatterAudit, useMatterDetail, usePaymentReceived, useResolveItem, useSideStep, useTransition, useUrgent } from './useMatter';
import { CasefileTab } from './casefile/CasefileTab';
import { LoiTab } from './loi/LoiTab';
import { BridgeTab } from './bridge/BridgeTab';
import { bridgeSubState } from './bridge/describe';
import { ReleaseTab } from './release/ReleaseTab';
import { ReportTab } from './report/ReportTab';

const TAB_IDS = ['overview', 'loi', 'casefile', 'booking', 'report', 'items', 'bridge', 'release', 'audit'] as const;
type TabId = (typeof TAB_IDS)[number];
const TAB_LABEL: Record<TabId, string> = { overview: 'Overview', loi: 'LOI', casefile: 'Casefile', booking: 'Booking', report: 'Report', items: 'Outstanding items', bridge: 'Bridge', release: 'Release', audit: 'Audit' };

function upcoming(appointments: Appointment[]): Appointment | undefined {
  return appointments.filter((a) => !a.attendance).sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0];
}

export function MatterHubPage() {
  useShellWidth('wide');
  const { ref = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const phone = useIsPhone();
  const online = useOnline();
  const { me } = useSession();
  const detail = useMatterDetail(ref);
  const audit = useMatterAudit(ref);
  const rawTab = params.get('tab');
  const tab: TabId = (TAB_IDS as readonly string[]).includes(rawTab ?? '') ? (rawTab as TabId) : 'overview';

  // An unknown tab falls back to Overview and the URL is rewritten in place.
  useEffect(() => {
    if (rawTab && rawTab !== tab) setParams((p) => { p.delete('tab'); return p; }, { replace: true });
  }, [rawTab, tab, setParams]);

  useEffect(() => {
    if (detail.data) recordRecent({ ref: detail.data.matter.ref, patientAsOnLoi: detail.data.matter.patientAsOnLoi, doctor: detail.data.matter.doctor, status: detail.data.matter.status, dueDate: detail.data.matter.dueDate });
  }, [detail.data]);

  const [form, setForm] = useState<TransitionOption | null>(null);
  const [booking, setBooking] = useState<TransitionOption | null>(null);
  const [conflict, setConflict] = useState(false);
  const [resumeConfirm, setResumeConfirm] = useState<TransitionOption | null>(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [busyEvent, setBusyEvent] = useState<StatusEvent['kind'] | null>(null);
  const [pendingStep, setPendingStep] = useState<SideStepKey | null>(null);

  const transition = useTransition(ref, {
    onConflict: () => setConflict(true),
    onMoved: () => {
      setForm(null);
      setBooking(null);
      setConflict(false);
      // The clicked button is gone with the old status; focus the new primary, else the title.
      requestAnimationFrame(() => (document.querySelector<HTMLElement>('[data-primary="true"]') ?? document.getElementById('page-title'))?.focus());
    },
  });
  const sideStep = useSideStep(ref);
  const addItem = useAddItem(ref);
  const resolveItem = useResolveItem(ref);
  const payment = usePaymentReceived(ref);
  const urgent = useUrgent(ref);

  // The form's option follows the record: after a conflict reload the from status and the guard may have changed.
  const liveForm = useMemo(() => (form && detail.data ? detail.data.options.find((o) => o.kind === form.kind) ?? form : form), [form, detail.data]);
  const liveBooking = useMemo(() => (booking && detail.data ? detail.data.options.find((o) => o.kind === booking.kind) ?? booking : booking), [booking, detail.data]);

  function fire(option: TransitionOption, extra: { reason?: string; items?: Array<{ description: string; followUpAt: string }>; appointment?: BookingValues } = {}) {
    if (!detail.data) return;
    setBusyEvent(option.kind);
    transition.mutate({ event: option.kind, expectedVersion: detail.data.matter.version, ...extra }, { onSettled: () => setBusyEvent(null) });
  }

  function onEvent(option: TransitionOption) {
    if (!option.allowed || !detail.data) return;
    if (BOOKING_KINDS.has(option.kind)) { setConflict(false); setBooking(option); return; }
    if (FORM_KINDS.has(option.kind)) { setConflict(false); setForm(option); return; }
    if (option.kind === 'resume' && detail.data.items.some((i) => !i.resolvedAt)) { setResumeConfirm(option); return; }
    fire(option);
  }

  const home = personaHome(me?.persona);
  if (detail.isError && detail.error instanceof ApiError && detail.error.status === 404) {
    return (
      <>
        <PageHeader title={ref} documentTitle={ref} />
        <MatterNotFound matterRef={ref} home={home} />
      </>
    );
  }

  const d = detail.data;
  // Failed with nothing cached: the header must not keep loading beside the error panel.
  const failed = detail.isError && !d;
  const openItems = d?.items.filter((i) => !i.resolvedAt).length ?? 0;
  // The Casefile tab counts what blocks the sort review's completion, while anything does (docs/ux/casefile.md).
  const tabs = TAB_IDS.map((id) => ({ value: id, label: TAB_LABEL[id], ...(id === 'items' && d ? { count: openItems } : {}), ...(id === 'casefile' && d?.sort && d.sort.blocking > 0 ? { count: d.sort.blocking } : {}), ...(id === 'loi' && d?.loi && d.loi.state !== 'approved' && d.loi.blocking > 0 ? { count: d.loi.blocking } : {}) }));
  const appt = d ? upcoming(d.appointments) : undefined;
  const layout = d ? layoutActions(d.matter.status, d.options) : null;
  const flags = d ? activeFlags({ ...d.matter.flags, urgent: d.cycle.flags.urgent }) : [];

  return (
    <>
      <PageHeader
        title={ref}
        documentTitle={ref}
        className="[&_h1]:text-[20px] [&_h1]:tabular md:[&_h1]:text-[20px]"
        description={d ? <span className="font-medium text-beam-text-soft">{d.matter.patientAsOnLoi}</span> : failed ? undefined : <span aria-hidden="true" className="inline-block h-4 w-40 animate-pulse rounded-md bg-beam-cream-deep" />}
        meta={d ? (
          <>
            <StatusChip status={d.matter.status} size="md" subState={gateSubState(d.matter) ?? bridgeSubState(d.cycle.bridge.mode)} />
            <span className="flex items-center gap-1"><DaysCell days={d.matter.daysWaiting} sla={d.matter.slaState} allowedDays={d.matter.allowedDays} status={d.matter.status} /> <span className="text-beam-text-muted">in status</span></span>
            <span>{d.matter.doctor.name}</span>
            <span>{d.matter.referrer.name}{d.matter.referrer.kind === 'plaintiff' ? <span className="text-beam-text-muted"> (plaintiff)</span> : null}</span>
            <span>{reportTypeLabel(d.matter.reportType)}</span>
            <span className="tabular">Due {shortDate(d.matter.dueDate)}</span>
            {d.matter.status >= 7 && d.cycle.rounds > 0 ? <span className="tabular">Round {d.cycle.rounds}</span> : null}
            {appt && (d.matter.status === 5 || d.matter.status === 14) ? <span className="tabular">Appointment {dateTime(appt.startsAt)}, {appt.clinicName}</span> : null}
            {flags.map((f) => <FlagText key={f} kind={f} />)}
          </>
        ) : failed ? undefined : <Skeleton className="h-7 w-64" />}
        actions={failed ? undefined : d && layout ? (
          phone ? (
            <PhoneActions matterRef={ref} status={d.matter.status} options={d.options} parkedFrom={d.cycle.parkedFrom} offline={!online} busyEvent={busyEvent} onEvent={onEvent} open={actionsOpen} onOpenChange={setActionsOpen} />
          ) : (
            <ActionCluster matterRef={ref} status={d.matter.status} options={d.options} parkedFrom={d.cycle.parkedFrom} offline={!online} busyEvent={busyEvent} onEvent={onEvent} />
          )
        ) : <div className="flex gap-2"><Skeleton className="h-9 w-32" /><Skeleton className="h-9 w-24" /></div>}
        tabs={<QueueTabs tabs={tabs} value={tab} onChange={(v) => { say(`${TAB_LABEL[v as TabId] ?? v}, ${ref}`); setParams((p) => { if (v === 'overview') p.delete('tab'); else p.set('tab', v); return p; }); }} label={`Tabs for ${ref}`} />}
        compact={d && layout ? (
          <>
            <span className="flex items-center gap-3">
              <span className="font-heading tabular hidden text-[18px] font-medium md:inline">{ref}</span>
              <StatusChip status={d.matter.status} size="sm" />
            </span>
            {!layout.terminal ? (
              <span className="flex items-center gap-2">
                {layout.primary ? (
                  <Button data-primary="true" size="default" className="h-11 md:h-8" disabled={!online || busyEvent !== null} onClick={() => onEvent(layout.primary!)}>
                    {busyEvent === layout.primary.kind ? EVENT_DISPLAY[layout.primary.kind].busy : eventLabel(layout.primary.kind)}
                  </Button>
                ) : null}
                {!phone ? <MoreMenu items={layout.more} parkedFrom={d.cycle.parkedFrom} offline={!online} onEvent={onEvent} size="compact" /> : null}
              </span>
            ) : null}
          </>
        ) : undefined}
      />

      <div className="mt-6">
        {failed ? (
          <ErrorPanel message="This case could not be loaded." onRetry={() => void detail.refetch()} />
        ) : tab === 'overview' ? (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
            <TransitionLog matterRef={ref} events={audit.data} isPending={audit.isPending} isError={audit.isError} onRetry={() => void audit.refetch()} />
            <div className="flex flex-col gap-6 lg:order-none order-first">
              {d ? (
                <>
                  <MatterRecordPanel detail={d} offline={!online} paying={payment.isPending} onMarkPaid={() => payment.mutate()} onUrgent={(u) => urgent.mutate(u)} urgentPending={urgent.isPending} />
                  <SideStepsChecklist
                    sideSteps={d.cycle.sideSteps}
                    offline={!online}
                    pendingKey={pendingStep}
                    onSet={(key, value, label) => { setPendingStep(key); sideStep.mutate({ key, value, label }, { onSettled: () => setPendingStep(null) }); }}
                  />
                  <OutstandingItemsSummary items={d.items} />
                </>
              ) : (
                <>
                  <Skeleton className="h-64 rounded-xl" />
                  <Skeleton className="h-48 rounded-xl" />
                </>
              )}
            </div>
          </div>
        ) : tab === 'items' ? (
          d ? <OutstandingItems items={d.items} offline={!online} adding={addItem.isPending} resolvingId={resolveItem.variables ?? null} onAdd={(v) => addItem.mutateAsync(v).then(() => undefined)} onResolve={(id) => resolveItem.mutate(id)} /> : <Skeleton className="h-40 rounded-xl" />
        ) : tab === 'audit' ? (
          <AuditTable matterRef={ref} events={audit.data} isPending={audit.isPending} isError={audit.isError} onRetry={() => void audit.refetch()} />
        ) : tab === 'casefile' ? (
          <CasefileTab matterRef={ref} detail={d} offline={!online} />
        ) : tab === 'loi' ? (
          <LoiTab matterRef={ref} detail={d} offline={!online} />
        ) : tab === 'report' ? (
          <ReportTab matterRef={ref} detail={d} offline={!online} />
        ) : tab === 'release' ? (
          <ReleaseTab matterRef={ref} detail={d} offline={!online} />
        ) : tab === 'bridge' ? (
          <BridgeTab matterRef={ref} detail={d} offline={!online} />
        ) : tab === 'booking' ? (
          <BookingTab matterRef={ref} detail={d} offline={!online} onEvent={onEvent} busyEvent={busyEvent} pendingStep={pendingStep} onSideStep={(key, value, label) => { setPendingStep(key); sideStep.mutate({ key, value, label }, { onSettled: () => setPendingStep(null) }); }} />
        ) : (
          <NotYetTab tab={tab} appointments={d?.appointments ?? []} />
        )}
      </div>

      {d ? (
        <TransitionForm
          matterRef={ref}
          from={d.matter.status}
          option={liveForm}
          appointment={appt}
          open={form !== null}
          onOpenChange={(open) => { if (!open) { setForm(null); setConflict(false); } }}
          busy={transition.isPending}
          conflict={conflict}
          offline={!online}
          onSubmit={(values) => liveForm && fire(liveForm, { reason: values.reason, items: values.items })}
        />
      ) : null}
      {d ? (
        <BookingForm
          matterRef={ref}
          from={d.matter.status}
          option={liveBooking}
          open={booking !== null}
          onOpenChange={(open) => { if (!open) { setBooking(null); setConflict(false); } }}
          busy={transition.isPending}
          conflict={conflict}
          offline={!online}
          onSubmit={(values) => liveBooking && fire(liveBooking, { appointment: values })}
        />
      ) : null}

      <AlertDialog open={resumeConfirm !== null} onOpenChange={(o) => { if (!o) setResumeConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{openItems} outstanding {openItems === 1 ? 'item is' : 'items are'} still open. Resume anyway?</AlertDialogTitle>
            <AlertDialogDescription>Resuming returns the matter to {d?.cycle.parkedFrom !== null && d?.cycle.parkedFrom !== undefined ? `${d.cycle.parkedFrom} ${statusLabel(d.cycle.parkedFrom)}` : 'where it was parked from'}. The items stay open until they are resolved.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep parked</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (resumeConfirm) fire(resumeConfirm); setResumeConfirm(null); }}>Resume</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
